import { formatToUSDate } from 'utils/dateTimeUtils/dateFormatUtil';
import getWarrantyDaysRemaining from 'utils/getWarrantyDaysRemaining';

export type UsageSummary = {
    acres: number;
    flightHours: number;
    flights: number;
};

export type Spec = {
    label: string;
    value: string;
};

export type DroneSpecs = {
    firmware: Spec;
    ids: Spec;
    usage: Spec;
    warranty: Spec;
};

type SpecLabels = {
    dronePrefix: string;
    emDash: string;
    faaPrefix: string;
    firmware: string;
    ids: string;
    unitAcres: string;
    unitDays: string;
    unitFlights: string;
    unitHoursShort: string;
    usage: string;
    warranty: string;
    warrantyExpPrefix: string;
    warrantyExpired: string;
};

const EXPIRED_THRESHOLD = 0;

const getDroneSpecs = ({
    faaRegistrationNumber,
    firmware,
    labels,
    now,
    serial,
    usage,
    warrantyEnd
}: {
    faaRegistrationNumber?: string;
    firmware: string;
    labels: SpecLabels;
    now: Date;
    serial?: string;
    usage: UsageSummary;
    warrantyEnd?: Date;
}): DroneSpecs => {
    const acres = Math.round(usage.acres).toLocaleString();
    const usageValue = `${usage.flightHours}${labels.unitHoursShort} · ${usage.flights} ${labels.unitFlights} · ${acres} ${labels.unitAcres}`;

    const getWarrantyValue = (): string => {
        if (!warrantyEnd) {
            return labels.emDash;
        }

        const daysRemaining = getWarrantyDaysRemaining({ end: warrantyEnd, now });

        if (daysRemaining <= EXPIRED_THRESHOLD) {
            return labels.warrantyExpired;
        }

        const expDate = formatToUSDate(new Date(warrantyEnd));

        return `${daysRemaining} ${labels.unitDays} · ${labels.warrantyExpPrefix} ${expDate}`;
    };

    const faaValue = faaRegistrationNumber ?? labels.emDash;
    const serialValue = serial ?? labels.emDash;
    const idsValue = `${labels.faaPrefix} ${faaValue} · ${labels.dronePrefix} ${serialValue}`;

    return {
        firmware: { label: labels.firmware, value: firmware },
        ids: { label: labels.ids, value: idsValue },
        usage: { label: labels.usage, value: usageValue },
        warranty: { label: labels.warranty, value: getWarrantyValue() }
    };
};

export default getDroneSpecs;
